import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import LoadingScreen from './LoadingScreen';
import {
  FaUniversity, FaSync, FaCheck, FaExclamationTriangle,
  FaTable, FaSpinner, FaTimesCircle, FaTrash, FaTimes, FaLock
} from 'react-icons/fa';
import EmailVerifyModal from './EmailVerifyModal';
import './Sidebar.css';
import './Navbar.css';
import './BankConnection.css';
import './EmailVerifyModal.css';

const BASE_URL = 'https://api.rside.app';

const CATEGORIES = [
  'Repair', 'Operation Expense', 'Utility', 'Maintenance',
  'Renovation', 'HOA', 'Tax', 'Insurance', 'Mortgage', 'Other',
];

function BankConnection() {
  const navigate = useNavigate();
  const [initials, setInitials]     = useState('');
  const [userEmail, setUserEmail]   = useState('');
  const [loading, setLoading]       = useState(true);
  const [properties, setProperties] = useState([]);

  const [connections, setConnections] = useState([]);

  const [connecting, setConnecting]           = useState(false);
  const [syncing, setSyncing]                 = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [connectionMsg, setConnectionMsg]     = useState('');

  const [transactions, setTransactions] = useState([]);
  const [txnLoading, setTxnLoading]     = useState(false);
  const [selectedIds, setSelectedIds]   = useState(new Set());

  // Single-select import form
  const [importForm, setImportForm] = useState({ category: '', notes: '', propertyId: '', doorId: '' });
  // Batch import forms: { [txn_id]: { category, propertyId, doorId } }
  const [batchForms, setBatchForms] = useState({});

  const [importing, setImporting]       = useState(false);
  const [importMsg, setImportMsg]       = useState({ type: '', text: '' });
  const [deletingId, setDeletingId]     = useState(null);
  const [hiddenTransactions, setHiddenTransactions] = useState([]);
  const [hidingId, setHidingId]         = useState(null);
  const [activeTab, setActiveTab]       = useState('pending');

  // Hidden tab selection + import
  const [hiddenSelectedIds, setHiddenSelectedIds] = useState(new Set());
  const [hiddenBatchForms, setHiddenBatchForms]   = useState({});
  const [hiddenImportForm, setHiddenImportForm]   = useState({ category: '', notes: '', propertyId: '', doorId: '' });
  const [hiddenImporting, setHiddenImporting]     = useState(false);
  const [hiddenImportMsg, setHiddenImportMsg]     = useState({ type: '', text: '' });

  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyContext, setVerifyContext] = useState('gate');
  const [emailVerified, setEmailVerified] = useState(false);
  const plaidReady = useRef(false);

  useEffect(() => {
    if (document.getElementById('plaid-link-sdk')) { plaidReady.current = true; return; }
    const s = document.createElement('script');
    s.id  = 'plaid-link-sdk';
    s.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
    s.onload = () => { plaidReady.current = true; };
    document.head.appendChild(s);
  }, []);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) { localStorage.removeItem('bc_gate_cleared'); return navigate('/login'); }
      try {
        const nameRes  = await fetch(`${BASE_URL}/PropertyManagerName?email=${encodeURIComponent(user.email)}`);
        const nameData = await nameRes.json();
        const parts    = nameData.Full_Name?.split(' ') || [];
        setInitials((parts[0]?.[0] || '') + (parts.at(-1)?.[0] || ''));
        setUserEmail(user.email);

        try {
          const stored = JSON.parse(localStorage.getItem('bc_gate_cleared') || 'null');
          if (stored?.uid === user.uid) setEmailVerified(true);
        } catch {}

        const propsRes = await fetch(`${BASE_URL}/GetAllProperties?full_name=${encodeURIComponent(nameData.Full_Name || '')}&Email=${encodeURIComponent(user.email)}`);
        if (propsRes.ok) {
          const pd = await propsRes.json();
          setProperties((pd.Properties || []).sort((a, b) => (a.Property_Name || '').localeCompare(b.Property_Name || '')));
        }
        await Promise.all([loadTransactions(user.email), loadConnections(user.email)]);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConnections = async (email) => {
    try {
      const res = await fetch(`${BASE_URL}/ListPlaidConnections?user_email=${encodeURIComponent(email)}`);
      if (res.ok) {
        const data = await res.json();
        setConnections(data.connections || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadTransactions = async (email) => {
    setTxnLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/ListPlaidTransactionsForReview?user_email=${encodeURIComponent(email)}`);
      if (res.ok) {
        const data = await res.json();
        const all = data.transactions || data.pending_review || [];
        setTransactions(all.filter(t => t.import_status !== 'hidden' && t.import_status !== 'imported'));
        setHiddenTransactions(all.filter(t => t.import_status === 'hidden'));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setTxnLoading(false);
    }
  };

  const handleConnectBank = async () => {
    if (!userEmail) return;
    await auth.currentUser?.reload();
    if (!auth.currentUser?.emailVerified) { setVerifyContext('connect'); setShowVerifyModal(true); return; }
    setConnecting(true);
    setConnectionStatus(null);
    setConnectionMsg('');
    try {
      const tokenRes = await fetch(`${BASE_URL}/CreatePlaidLinkToken?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { accept: 'application/json' },
      });
      if (!tokenRes.ok) throw new Error('Could not create link token');
      const tokenData = await tokenRes.json();
      const linkToken = tokenData.link_token;
      if (!linkToken) throw new Error('No link token returned from server');

      const handler = window.Plaid.create({
        token: linkToken,
        onSuccess: async (publicToken) => {
          try {
            const exRes = await fetch(`${BASE_URL}/ExchangePlaidPublicToken?user_email=${encodeURIComponent(userEmail)}&public_token=${encodeURIComponent(publicToken)}`, {
              method: 'POST',
              headers: { accept: 'application/json' },
            });
            if (!exRes.ok) throw new Error('Failed to exchange token');
            const exData = await exRes.json();
            const connId = exData.plaid_connection_id;

            setSyncing(true);
            const syncRes = await fetch(`${BASE_URL}/SyncPlaidTransactions?user_email=${encodeURIComponent(userEmail)}&plaid_connection_id=${encodeURIComponent(connId)}`, {
              method: 'POST',
              headers: { accept: 'application/json' },
            });
            if (!syncRes.ok) throw new Error('Failed to sync transactions');

            setSyncing(false);
            setConnectionStatus('success');
            setConnectionMsg('Bank connected! Transactions synced and ready to review below.');
            await Promise.all([loadTransactions(userEmail), loadConnections(userEmail)]);
          } catch (err) {
            setSyncing(false);
            setConnectionStatus('error');
            setConnectionMsg(err.message || 'Something went wrong after connecting');
          } finally {
            document.body.style.overflow = '';
            setConnecting(false);
          }
        },
        onExit: (err) => {
          document.body.style.overflow = '';
          setConnecting(false);
          setSyncing(false);
          if (err) {
            setConnectionStatus('error');
            setConnectionMsg(err.display_message || 'Bank connection was cancelled or failed');
          }
        },
      });
      document.body.style.overflow = 'hidden';
      handler.open();
    } catch (err) {
      setConnecting(false);
      setConnectionStatus('error');
      setConnectionMsg(err.message || 'Failed to start bank connection');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setBatchForms(p => { const n = { ...p }; delete n[id]; return n; });
      } else {
        next.add(id);
        setBatchForms(p => ({ ...p, [id]: { category: '', propertyId: '', doorId: '' } }));
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === transactions.length) {
      setSelectedIds(new Set());
      setBatchForms({});
    } else {
      const newIds = new Set(transactions.map(t => t.transaction_id ?? t.id));
      setSelectedIds(newIds);
      const newForms = {};
      for (const id of newIds) newForms[id] = { category: '', propertyId: '', doorId: '' };
      setBatchForms(newForms);
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setBatchForms({});
    setImportForm({ category: '', notes: '', propertyId: '', doorId: '' });
    setImportMsg({ type: '', text: '' });
  };

  const toggleHiddenSelect = (id) => {
    setHiddenSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setHiddenBatchForms(p => { const n = { ...p }; delete n[id]; return n; });
      } else {
        next.add(id);
        setHiddenBatchForms(p => ({ ...p, [id]: { category: '', propertyId: '', doorId: '' } }));
      }
      return next;
    });
  };

  const toggleHiddenAll = () => {
    if (hiddenSelectedIds.size === hiddenTransactions.length) {
      setHiddenSelectedIds(new Set());
      setHiddenBatchForms({});
    } else {
      const newIds = new Set(hiddenTransactions.map(t => t.transaction_id ?? t.id));
      setHiddenSelectedIds(newIds);
      const newForms = {};
      for (const id of newIds) newForms[id] = { category: '', propertyId: '', doorId: '' };
      setHiddenBatchForms(newForms);
    }
  };

  const clearHiddenSelection = () => {
    setHiddenSelectedIds(new Set());
    setHiddenBatchForms({});
    setHiddenImportForm({ category: '', notes: '', propertyId: '', doorId: '' });
    setHiddenImportMsg({ type: '', text: '' });
  };

  const handleHiddenImport = async () => {
    if (!hiddenSelectedIds.size) return;
    if (!hiddenImportForm.category)   { setHiddenImportMsg({ type: 'error', text: 'Please select a category' }); return; }
    if (!hiddenImportForm.propertyId) { setHiddenImportMsg({ type: 'error', text: 'Please select a property' });  return; }
    setHiddenImporting(true);
    setHiddenImportMsg({ type: '', text: '' });
    try {
      const params = new URLSearchParams({
        user_email:  userEmail,
        category:    hiddenImportForm.category,
        notes:       hiddenImportForm.notes,
        property_id: Number(hiddenImportForm.propertyId),
        door_id:     hiddenImportForm.doorId ? Number(hiddenImportForm.doorId) : 0,
      });
      const res = await fetch(`${BASE_URL}/ImportSelectedPlaidTransactions?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', accept: 'application/json' },
        body: JSON.stringify([...hiddenSelectedIds]),
      });
      if (!res.ok) throw new Error('Import failed. Please try again.');
      setHiddenImportMsg({ type: 'success', text: 'Transaction imported successfully!' });
      clearHiddenSelection();
      await loadTransactions(userEmail);
    } catch (err) {
      setHiddenImportMsg({ type: 'error', text: err.message || 'Failed to import' });
    } finally {
      setHiddenImporting(false);
    }
  };

  const handleHiddenBatchImport = async () => {
    const ids = [...hiddenSelectedIds];
    for (const id of ids) {
      const f = hiddenBatchForms[id];
      if (!f?.category)   { setHiddenImportMsg({ type: 'error', text: 'Please select a category for every transaction' }); return; }
      if (!f?.propertyId) { setHiddenImportMsg({ type: 'error', text: 'Please select a property for every transaction' });  return; }
    }
    setHiddenImporting(true);
    setHiddenImportMsg({ type: '', text: '' });
    try {
      const groups = {};
      for (const id of ids) {
        const f   = hiddenBatchForms[id];
        const key = `${f.category}::${f.propertyId}::${f.doorId}`;
        if (!groups[key]) groups[key] = { category: f.category, propertyId: f.propertyId, doorId: f.doorId, ids: [] };
        groups[key].ids.push(id);
      }
      for (const g of Object.values(groups)) {
        const batchParams = new URLSearchParams({
          user_email:  userEmail,
          category:    g.category,
          notes:       '',
          property_id: Number(g.propertyId),
          door_id:     g.doorId ? Number(g.doorId) : 0,
        });
        const res = await fetch(`${BASE_URL}/ImportSelectedPlaidTransactions?${batchParams}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', accept: 'application/json' },
          body: JSON.stringify(g.ids),
        });
        if (!res.ok) throw new Error('Batch import failed');
      }
      setHiddenImportMsg({ type: 'success', text: `${ids.length} transactions imported successfully!` });
      clearHiddenSelection();
      await loadTransactions(userEmail);
    } catch (err) {
      setHiddenImportMsg({ type: 'error', text: err.message || 'Failed to import' });
    } finally {
      setHiddenImporting(false);
    }
  };

  const handleDelete = async (txnId, e) => {
    e.stopPropagation();
    setDeletingId(txnId);
    try {
      const res = await fetch(`${BASE_URL}/DeletePlaidTransaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: userEmail, transaction_id: txnId }),
      });
      if (!res.ok) throw new Error('Delete failed');
      setTransactions(prev => prev.filter(t => (t.transaction_id ?? t.id) !== txnId));
      setSelectedIds(prev => { const n = new Set(prev); n.delete(txnId); return n; });
      setBatchForms(prev => { const n = { ...prev }; delete n[txnId]; return n; });
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleImport = async () => {
    if (!selectedIds.size) return;
    if (!importForm.category)   { setImportMsg({ type: 'error', text: 'Please select a category' }); return; }
    if (!importForm.propertyId) { setImportMsg({ type: 'error', text: 'Please select a property' });  return; }

    setImporting(true);
    setImportMsg({ type: '', text: '' });
    try {
      const impParams = new URLSearchParams({
        user_email:  userEmail,
        category:    importForm.category,
        notes:       importForm.notes,
        property_id: Number(importForm.propertyId),
        door_id:     importForm.doorId ? Number(importForm.doorId) : 0,
      });
      const res = await fetch(`${BASE_URL}/ImportSelectedPlaidTransactions?${impParams}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', accept: 'application/json' },
        body: JSON.stringify([...selectedIds]),
      });
      if (!res.ok) throw new Error('Import failed. Please try again.');
      setImportMsg({ type: 'success', text: `Transaction imported successfully!` });
      clearSelection();
      await loadTransactions(userEmail);
    } catch (err) {
      setImportMsg({ type: 'error', text: err.message || 'Failed to import' });
    } finally {
      setImporting(false);
    }
  };

  const handleBatchImport = async () => {
    const ids = [...selectedIds];
    for (const id of ids) {
      const f = batchForms[id];
      if (!f?.category)   { setImportMsg({ type: 'error', text: 'Please select a category for every transaction' }); return; }
      if (!f?.propertyId) { setImportMsg({ type: 'error', text: 'Please select a property for every transaction' });  return; }
    }
    setImporting(true);
    setImportMsg({ type: '', text: '' });
    try {
      // Group transactions by category + property + door to minimize API calls
      const groups = {};
      for (const id of ids) {
        const f   = batchForms[id];
        const key = `${f.category}::${f.propertyId}::${f.doorId}`;
        if (!groups[key]) groups[key] = { category: f.category, propertyId: f.propertyId, doorId: f.doorId, ids: [] };
        groups[key].ids.push(id);
      }
      for (const g of Object.values(groups)) {
        const batchParams = new URLSearchParams({
          user_email:  userEmail,
          category:    g.category,
          notes:       '',
          property_id: Number(g.propertyId),
          door_id:     g.doorId ? Number(g.doorId) : 0,
        });
        const res = await fetch(`${BASE_URL}/ImportSelectedPlaidTransactions?${batchParams}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', accept: 'application/json' },
          body: JSON.stringify(g.ids),
        });
        if (!res.ok) throw new Error('Batch import failed');
      }
      setImportMsg({ type: 'success', text: `${ids.length} transactions imported successfully!` });
      clearSelection();
      await loadTransactions(userEmail);
    } catch (err) {
      setImportMsg({ type: 'error', text: err.message || 'Failed to import' });
    } finally {
      setImporting(false);
    }
  };

  const uniqueProps   = [...new Map(properties.map(p => [p.Property_ID, p])).values()];
  const doorsForProp  = properties.filter(p => String(p.Property_ID) === String(importForm.propertyId));
  const isMultiFamily = doorsForProp.length > 1;

  const hiddenDoorsForProp  = properties.filter(p => String(p.Property_ID) === String(hiddenImportForm.propertyId));
  const isHiddenMultiFamily = hiddenDoorsForProp.length > 1;
  const isHiddenBatch       = hiddenSelectedIds.size > 1;

  const handleHide = async (txnId, e) => {
    e.stopPropagation();
    setHidingId(txnId);
    try {
      const res = await fetch(`${BASE_URL}/HideSelectedPlaidTransactions?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', accept: 'application/json' },
        body: JSON.stringify([txnId]),
      });
      if (!res.ok) throw new Error('Hide failed');
      const txn = transactions.find(t => (t.transaction_id ?? t.id) === txnId);
      setTransactions(prev => prev.filter(t => (t.transaction_id ?? t.id) !== txnId));
      if (txn) setHiddenTransactions(prev => [{ ...txn, status: 'hidden' }, ...prev]);
      setSelectedIds(prev => { const n = new Set(prev); n.delete(txnId); return n; });
      setBatchForms(prev => { const n = { ...prev }; delete n[txnId]; return n; });
    } catch (err) {
      console.error('Hide error:', err);
    } finally {
      setHidingId(null);
    }
  };

  const fmtAmt = (amt) =>
    `${amt >= 0 ? '-' : '+'}$${Math.abs(amt).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  if (loading) return <LoadingScreen />;

  const isBatch = selectedIds.size > 1;

  return (
    <div className="home-page">
      {showVerifyModal && (
        <EmailVerifyModal
          requireSend={verifyContext === 'gate'}
          onVerified={() => {
            setShowVerifyModal(false);
            setEmailVerified(true);
            const user = auth.currentUser;
            if (user) {
              localStorage.setItem('bc_gate_cleared', JSON.stringify({ uid: user.uid }));
            }
            if (verifyContext === 'connect') handleConnectBank();
          }}
          onCancel={() => setShowVerifyModal(false)}
        />
      )}
      <Sidebar initials={initials} />
      <div className="main-content">
        <Navbar title="Bank Connection" />
        <div className="bc-page">

          {/* Page header */}
          <div className="bc-header-card">
            <div className="bc-header-left">
              <h1 className="bc-page-title">Bank Connection</h1>
              <p className="bc-page-sub">Connect your bank to automatically sync and import transactions</p>
            </div>
            <div className="bc-header-right">
              <button className="bc-connect-btn" onClick={handleConnectBank} disabled={connecting || syncing}>
                {(connecting || syncing)
                  ? <><FaSpinner className="bc-spin" /> {syncing ? 'Syncing…' : 'Connecting…'}</>
                  : <><FaUniversity /> Connect Bank</>}
              </button>
              <div className="bc-header-stat">
                <span className="bc-header-stat-label">PENDING REVIEW</span>
                <span className="bc-header-stat-val">{transactions.length}</span>
              </div>
            </div>
          </div>

          {/* Connect Bank card */}
          <div className="bc-connect-card">
            <div className="bc-connect-left">
              <div className="bc-bank-icon-wrap">
                <FaUniversity className="bc-bank-icon" />
              </div>
              <div>
                <h2 className="bc-connect-title">Securely Link a Bank Account</h2>
                <p className="bc-connect-desc">
                  Connect via Plaid — your credentials are never stored on our servers.
                  Once connected, transactions sync automatically for review.
                </p>
              </div>
            </div>

            {/* Connected accounts */}
            {connections.length > 0 && (
              <div className="bc-connections-list">
                {connections.map(conn => (
                  <div key={conn.plaid_connection_id} className="bc-connection-row">
                    <div className="bc-connection-info">
                      <span className={`bc-connection-status ${conn.status === 'active' ? 'bc-status-active' : 'bc-status-inactive'}`}>
                        {conn.status}
                      </span>
                      <span className="bc-connection-accounts">
                        {conn.accounts.map(acc => (
                          <span key={acc.plaid_account_id} className="bc-account-chip">
                            <FaUniversity className="bc-account-chip-icon" />
                            {acc.account_name} ···{acc.account_mask}
                          </span>
                        ))}
                      </span>
                    </div>
                    <span className="bc-connection-date">
                      Connected {new Date(conn.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Transactions Review */}
          <div className="bc-review-card">
            <div className="bc-review-header">
              <div>
                <h2 className="bc-review-title">Transactions Review</h2>
                <p className="bc-review-sub">
                  {transactions.length > 0
                    ? `${transactions.length} pending transaction${transactions.length !== 1 ? 's' : ''} ready for review`
                    : 'No pending transactions yet'}
                </p>
              </div>
              {connectionStatus === 'success' && (
                <div className="bc-banner bc-banner-success"><FaCheck /> {connectionMsg}</div>
              )}
              {connectionStatus === 'error' && (
                <div className="bc-banner bc-banner-error"><FaExclamationTriangle /> {connectionMsg}</div>
              )}
              {syncing && (
                <div className="bc-banner bc-banner-syncing"><FaSpinner className="bc-spin" /> Syncing transactions, please wait…</div>
              )}
              <button className="bc-refresh-btn" onClick={() => loadTransactions(userEmail)} disabled={txnLoading} title="Refresh">
                <FaSync className={txnLoading ? 'bc-spin' : ''} />
                {txnLoading ? ' Refreshing…' : ' Refresh'}
              </button>
            </div>

            {/* ── TABS ── */}
            <div className="bc-tabs">
              <button
                className={`bc-tab ${activeTab === 'pending' ? 'bc-tab-active' : ''}`}
                onClick={() => setActiveTab('pending')}
              >
                Pending Review
                {transactions.length > 0 && <span className="bc-tab-badge">{transactions.length}</span>}
              </button>
              <button
                className={`bc-tab ${activeTab === 'hidden' ? 'bc-tab-active' : ''}`}
                onClick={() => setActiveTab('hidden')}
              >
                Hidden
                {hiddenTransactions.length > 0 && <span className="bc-tab-badge bc-tab-badge-gray">{hiddenTransactions.length}</span>}
              </button>
            </div>

            {emailVerified === false ? (
              <div className="bc-verify-gate">
                <div className="bc-verify-gate-left">
                  <div className="bc-verify-gate-icon-wrap">
                    <FaLock className="bc-verify-gate-icon" />
                  </div>
                  <div>
                    <h3 className="bc-verify-gate-title">Email Verification Required</h3>
                    <p className="bc-verify-gate-desc">
                      Please verify your email address to access your transactions and bank data.
                    </p>
                  </div>
                </div>
                <button className="bc-connect-btn" onClick={() => { setVerifyContext('gate'); setShowVerifyModal(true); }}>
                  <FaLock /> Verify Email
                </button>
              </div>
            ) : txnLoading ? (
              <div className="bc-state-row">
                <FaSpinner className="bc-spin bc-state-icon" />
                <span>Loading transactions…</span>
              </div>
            ) : activeTab === 'hidden' ? (
              hiddenTransactions.length === 0 ? (
                <div className="bc-empty-state">
                  <FaTable className="bc-empty-icon" />
                  <p className="bc-empty-text">No hidden transactions.</p>
                </div>
              ) : (
                <>
                  {/* ── HIDDEN IMPORT PANEL ── */}
                  {hiddenSelectedIds.size > 0 && (
                    <div className="bc-import-panel">
                      {isHiddenBatch ? (
                        <>
                          <div className="bc-import-panel-hd">
                            <h3 className="bc-import-heading">Batch Import — {hiddenSelectedIds.size} Transactions</h3>
                            <button className="bc-cancel-sel-btn" onClick={clearHiddenSelection} disabled={hiddenImporting}>Clear</button>
                          </div>
                          <div className="bc-batch-list">
                            {[...hiddenSelectedIds].map(id => {
                              const txn  = hiddenTransactions.find(t => (t.transaction_id ?? t.id) === id);
                              if (!txn) return null;
                              const amt  = txn.amount ?? 0;
                              const form = hiddenBatchForms[id] || { category: '', propertyId: '', doorId: '' };
                              const doors   = properties.filter(p => String(p.Property_ID) === String(form.propertyId));
                              const isMulti = doors.length > 1;
                              return (
                                <div key={id} className="bc-batch-row">
                                  <div className="bc-batch-txn-info">
                                    <span className="bc-batch-desc">{txn.name || txn.description || '—'}</span>
                                    <span className="bc-batch-meta">
                                      {txn.transaction_date || txn.date}&nbsp;·&nbsp;
                                      <span className={amt >= 0 ? 'bc-amt-neg' : 'bc-amt-pos'}>{fmtAmt(amt)}</span>
                                    </span>
                                  </div>
                                  <div className="bc-batch-selects">
                                    <select
                                      className="bc-batch-sel"
                                      value={form.category}
                                      onChange={e => setHiddenBatchForms(p => ({ ...p, [id]: { ...p[id], category: e.target.value } }))}
                                    >
                                      <option value="">Category *</option>
                                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                    <select
                                      className="bc-batch-sel"
                                      value={form.propertyId}
                                      onChange={e => setHiddenBatchForms(p => ({ ...p, [id]: { ...p[id], propertyId: e.target.value, doorId: '' } }))}
                                    >
                                      <option value="">Property *</option>
                                      {uniqueProps.map(p => <option key={p.Property_ID} value={p.Property_ID}>{p.Property_Name}</option>)}
                                    </select>
                                    {isMulti && (
                                      <select
                                        className="bc-batch-sel"
                                        value={form.doorId}
                                        onChange={e => setHiddenBatchForms(p => ({ ...p, [id]: { ...p[id], doorId: e.target.value } }))}
                                      >
                                        <option value="">All units</option>
                                        {doors.map(d => <option key={d.Door_id} value={d.Door_id}>Unit {d.Unit_Number}</option>)}
                                      </select>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {hiddenImportMsg.text && (
                            <div className={`bc-import-msg ${hiddenImportMsg.type === 'success' ? 'bc-import-msg-ok' : 'bc-import-msg-err'}`}>
                              {hiddenImportMsg.type === 'success' ? <FaCheck /> : <FaTimesCircle />} {hiddenImportMsg.text}
                            </div>
                          )}
                          <div className="bc-import-footer">
                            <button className="bc-import-btn" onClick={handleHiddenBatchImport} disabled={hiddenImporting}>
                              {hiddenImporting
                                ? <><FaSpinner className="bc-spin" /> Importing…</>
                                : `Import All ${hiddenSelectedIds.size} Transactions`}
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="bc-import-panel-hd">
                            <h3 className="bc-import-heading">Import Selected Transaction</h3>
                            <button className="bc-cancel-sel-btn" onClick={clearHiddenSelection} disabled={hiddenImporting}>Clear</button>
                          </div>
                          <div className="bc-import-fields">
                            <div className="bc-field">
                              <label>Category *</label>
                              <select value={hiddenImportForm.category} onChange={e => setHiddenImportForm(p => ({ ...p, category: e.target.value }))}>
                                <option value="">Select category</option>
                                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </div>
                            <div className="bc-field">
                              <label>Property *</label>
                              <select value={hiddenImportForm.propertyId} onChange={e => setHiddenImportForm(p => ({ ...p, propertyId: e.target.value, doorId: '' }))}>
                                <option value="">Select property</option>
                                {uniqueProps.map(p => <option key={p.Property_ID} value={p.Property_ID}>{p.Property_Name}</option>)}
                              </select>
                            </div>
                            {isHiddenMultiFamily && (
                              <div className="bc-field">
                                <label>Unit</label>
                                <select value={hiddenImportForm.doorId} onChange={e => setHiddenImportForm(p => ({ ...p, doorId: e.target.value }))}>
                                  <option value="">All units (split)</option>
                                  {hiddenDoorsForProp.map(d => <option key={d.Door_id} value={d.Door_id}>Unit {d.Unit_Number}</option>)}
                                </select>
                              </div>
                            )}
                            <div className="bc-field bc-field-wide">
                              <label>Notes</label>
                              <input
                                type="text"
                                placeholder="Optional notes…"
                                value={hiddenImportForm.notes}
                                onChange={e => setHiddenImportForm(p => ({ ...p, notes: e.target.value }))}
                              />
                            </div>
                          </div>
                          {hiddenImportMsg.text && (
                            <div className={`bc-import-msg ${hiddenImportMsg.type === 'success' ? 'bc-import-msg-ok' : 'bc-import-msg-err'}`}>
                              {hiddenImportMsg.type === 'success' ? <FaCheck /> : <FaTimesCircle />} {hiddenImportMsg.text}
                            </div>
                          )}
                          <div className="bc-import-footer">
                            <button className="bc-import-btn" onClick={handleHiddenImport} disabled={hiddenImporting}>
                              {hiddenImporting
                                ? <><FaSpinner className="bc-spin" /> Importing…</>
                                : 'Import Transaction'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* ── HIDDEN TRANSACTION TABLE ── */}
                  <div className="bc-table-wrap">
                    <div className="bc-grid">
                      <div className="bc-grid-row bc-grid-hd">
                        <span className="bc-col-check">
                          <input
                            type="checkbox"
                            checked={hiddenSelectedIds.size === hiddenTransactions.length && hiddenTransactions.length > 0}
                            onChange={toggleHiddenAll}
                          />
                        </span>
                        <span>DATE</span>
                        <span>DESCRIPTION</span>
                        <span>MERCHANT</span>
                        <span>AMOUNT</span>
                        <span></span>
                      </div>
                      {hiddenTransactions.map(txn => {
                        const id       = txn.transaction_id ?? txn.id;
                        const selected = hiddenSelectedIds.has(id);
                        const amt      = txn.amount ?? 0;
                        return (
                          <div
                            key={id}
                            className={`bc-grid-row bc-grid-data bc-row-hidden${selected ? ' bc-row-sel' : ''}`}
                            onClick={() => toggleHiddenSelect(id)}
                          >
                            <span className="bc-col-check" onClick={e => e.stopPropagation()}>
                              <input type="checkbox" checked={selected} onChange={() => toggleHiddenSelect(id)} />
                            </span>
                            <span className="bc-col-date">{txn.transaction_date || txn.date || '—'}</span>
                            <span className="bc-col-desc">{txn.name || txn.description || '—'}</span>
                            <span>{txn.merchant_name || txn.merchant || '—'}</span>
                            <span className={amt >= 0 ? 'bc-amt-neg' : 'bc-amt-pos'}>{fmtAmt(amt)}</span>
                            <span></span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )
            ) : transactions.length === 0 ? (
              <div className="bc-empty-state">
                <FaTable className="bc-empty-icon" />
                <p className="bc-empty-text">No transactions pending review.</p>
                <p className="bc-empty-hint">Connect a bank account above to sync transactions.</p>
              </div>
            ) : (
              <>
                {/* ── IMPORT PANEL — pinned above the table ── */}
                {selectedIds.size > 0 && (
                  <div className="bc-import-panel">
                    {isBatch ? (
                      /* ── BATCH MODE ── */
                      <>
                        <div className="bc-import-panel-hd">
                          <h3 className="bc-import-heading">Batch Import — {selectedIds.size} Transactions</h3>
                          <button className="bc-cancel-sel-btn" onClick={clearSelection} disabled={importing}>Clear</button>
                        </div>

                        <div className="bc-batch-list">
                          {[...selectedIds].map(id => {
                            const txn = transactions.find(t => (t.transaction_id ?? t.id) === id);
                            if (!txn) return null;
                            const amt  = txn.amount ?? 0;
                            const form = batchForms[id] || { category: '', propertyId: '', doorId: '' };
                            const doors   = properties.filter(p => String(p.Property_ID) === String(form.propertyId));
                            const isMulti = doors.length > 1;
                            return (
                              <div key={id} className="bc-batch-row">
                                <div className="bc-batch-txn-info">
                                  <span className="bc-batch-desc">{txn.name || txn.description || '—'}</span>
                                  <span className="bc-batch-meta">
                                    {txn.transaction_date || txn.date}&nbsp;·&nbsp;
                                    <span className={amt >= 0 ? 'bc-amt-neg' : 'bc-amt-pos'}>{fmtAmt(amt)}</span>
                                  </span>
                                </div>
                                <div className="bc-batch-selects">
                                  <select
                                    className="bc-batch-sel"
                                    value={form.category}
                                    onChange={e => setBatchForms(p => ({ ...p, [id]: { ...p[id], category: e.target.value } }))}
                                  >
                                    <option value="">Category *</option>
                                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                  </select>
                                  <select
                                    className="bc-batch-sel"
                                    value={form.propertyId}
                                    onChange={e => setBatchForms(p => ({ ...p, [id]: { ...p[id], propertyId: e.target.value, doorId: '' } }))}
                                  >
                                    <option value="">Property *</option>
                                    {uniqueProps.map(p => <option key={p.Property_ID} value={p.Property_ID}>{p.Property_Name}</option>)}
                                  </select>
                                  {isMulti && (
                                    <select
                                      className="bc-batch-sel"
                                      value={form.doorId}
                                      onChange={e => setBatchForms(p => ({ ...p, [id]: { ...p[id], doorId: e.target.value } }))}
                                    >
                                      <option value="">All units</option>
                                      {doors.map(d => <option key={d.Door_id} value={d.Door_id}>Unit {d.Unit_Number}</option>)}
                                    </select>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {importMsg.text && (
                          <div className={`bc-import-msg ${importMsg.type === 'success' ? 'bc-import-msg-ok' : 'bc-import-msg-err'}`}>
                            {importMsg.type === 'success' ? <FaCheck /> : <FaTimesCircle />} {importMsg.text}
                          </div>
                        )}

                        <div className="bc-import-footer">
                          <button className="bc-import-btn" onClick={handleBatchImport} disabled={importing}>
                            {importing
                              ? <><FaSpinner className="bc-spin" /> Importing…</>
                              : `Import All ${selectedIds.size} Transactions`}
                          </button>
                        </div>
                      </>
                    ) : (
                      /* ── SINGLE MODE ── */
                      <>
                        <div className="bc-import-panel-hd">
                          <h3 className="bc-import-heading">Import Selected Transaction</h3>
                          <button className="bc-cancel-sel-btn" onClick={clearSelection} disabled={importing}>Clear</button>
                        </div>

                        <div className="bc-import-fields">
                          <div className="bc-field">
                            <label>Category *</label>
                            <select value={importForm.category} onChange={e => setImportForm(p => ({ ...p, category: e.target.value }))}>
                              <option value="">Select category</option>
                              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div className="bc-field">
                            <label>Property *</label>
                            <select value={importForm.propertyId} onChange={e => setImportForm(p => ({ ...p, propertyId: e.target.value, doorId: '' }))}>
                              <option value="">Select property</option>
                              {uniqueProps.map(p => <option key={p.Property_ID} value={p.Property_ID}>{p.Property_Name}</option>)}
                            </select>
                          </div>
                          {isMultiFamily && (
                            <div className="bc-field">
                              <label>Unit</label>
                              <select value={importForm.doorId} onChange={e => setImportForm(p => ({ ...p, doorId: e.target.value }))}>
                                <option value="">All units (split)</option>
                                {doorsForProp.map(d => <option key={d.Door_id} value={d.Door_id}>Unit {d.Unit_Number}</option>)}
                              </select>
                            </div>
                          )}
                          <div className="bc-field bc-field-wide">
                            <label>Notes</label>
                            <input
                              type="text"
                              placeholder="Optional notes…"
                              value={importForm.notes}
                              onChange={e => setImportForm(p => ({ ...p, notes: e.target.value }))}
                            />
                          </div>
                        </div>

                        {importMsg.text && (
                          <div className={`bc-import-msg ${importMsg.type === 'success' ? 'bc-import-msg-ok' : 'bc-import-msg-err'}`}>
                            {importMsg.type === 'success' ? <FaCheck /> : <FaTimesCircle />} {importMsg.text}
                          </div>
                        )}

                        <div className="bc-import-footer">
                          <button className="bc-import-btn" onClick={handleImport} disabled={importing}>
                            {importing
                              ? <><FaSpinner className="bc-spin" /> Importing…</>
                              : 'Import Transaction'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* ── TRANSACTION TABLE ── */}
                <div className="bc-table-wrap">
                  <div className="bc-grid">
                    <div className="bc-grid-row bc-grid-hd">
                      <span className="bc-col-check">
                        <input
                          type="checkbox"
                          checked={selectedIds.size === transactions.length && transactions.length > 0}
                          onChange={toggleAll}
                        />
                      </span>
                      <span>DATE</span>
                      <span>DESCRIPTION</span>
                      <span>MERCHANT</span>
                      <span>AMOUNT</span>
                      <span></span>
                    </div>

                    {transactions.map(txn => {
                      const id       = txn.transaction_id ?? txn.id;
                      const selected = selectedIds.has(id);
                      const amt      = txn.amount ?? 0;
                      return (
                        <div
                          key={id}
                          className={`bc-grid-row bc-grid-data${selected ? ' bc-row-sel' : ''}`}
                          onClick={() => toggleSelect(id)}
                        >
                          <span className="bc-col-check" onClick={e => e.stopPropagation()}>
                            <input type="checkbox" checked={selected} onChange={() => toggleSelect(id)} />
                          </span>
                          <span className="bc-col-date">{txn.transaction_date || txn.date || '—'}</span>
                          <span className="bc-col-desc">{txn.name || txn.description || '—'}</span>
                          <span>{txn.merchant_name || txn.merchant || '—'}</span>
                          <span className={amt >= 0 ? 'bc-amt-neg' : 'bc-amt-pos'}>{fmtAmt(amt)}</span>
                          <span className="bc-col-action" onClick={e => e.stopPropagation()}>
                            <button
                              className="bc-hide-btn"
                              onClick={e => handleHide(id, e)}
                              disabled={hidingId === id}
                              title="Hide transaction"
                            >
                              {hidingId === id ? <FaSpinner className="bc-spin" /> : <FaTimes />}
                            </button>
                            <button
                              className="bc-delete-btn"
                              onClick={e => handleDelete(id, e)}
                              disabled={deletingId === id}
                              title="Delete permanently"
                            >
                              {deletingId === id ? <FaSpinner className="bc-spin" /> : <FaTrash />}
                            </button>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BankConnection;
